import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, FileText, Loader2, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useDocuments } from '@/hooks/useDocuments';
import { useCIMTeaserActions, useCIMTeaserJob } from '@/hooks/useCIMTeaserAnalysis';
import { cn } from '@/lib/utils';
import { validateCIMTeaserFiles } from './cimTeaserValidation';
import { toFriendlyTeaserError } from './teaserErrorMessages';

type FlowStep = 'idle' | 'creating' | 'uploading' | 'queued' | 'processing' | 'complete' | 'failed';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv';

function flowLabel(step: FlowStep): string {
  switch (step) {
    case 'creating':
      return 'Creating screening project';
    case 'uploading':
      return 'Uploading documents';
    case 'queued':
      return 'Queued for analysis';
    case 'processing':
      return 'Generating teaser';
    case 'complete':
      return 'Opening report';
    case 'failed':
      return 'Analysis failed';
    default:
      return 'Ready for upload';
  }
}

export const CIMTeaserDashboardCard: React.FC = () => {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [projectId, setProjectId] = useState<string | undefined>();
  const [jobId, setJobId] = useState<string | undefined>();
  const [step, setStep] = useState<FlowStep>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { uploadDocument } = useDocuments();
  const { createTeaserProject, startTeaserAnalysis } = useCIMTeaserActions();
  const { job } = useCIMTeaserJob(projectId, jobId);

  const jobProgress = job?.progress?.percent ?? 0;
  const progress = step === 'uploading'
    ? uploadProgress
    : step === 'processing' || step === 'queued'
      ? Math.max(35, jobProgress)
      : step === 'complete'
        ? 100
        : step === 'creating'
          ? 15
          : 0;

  useEffect(() => {
    if (!job) return;
    if (job.status === 'queued') setStep('queued');
    if (job.status === 'processing') setStep('processing');
    if (job.status === 'failed') {
      setStep('failed');
      setLocalError(toFriendlyTeaserError(job.error, 'CIM teaser analysis failed.'));
    }
    if (job.status === 'completed' && job.result?.teaserId && projectId) {
      setStep('complete');
      navigate(`/projects/${projectId}/cim-teaser/${job.result.teaserId}`);
    }
  }, [job, navigate, projectId]);

  const handleFilesSelected = (selected: File[]) => {
    const validation = validateCIMTeaserFiles(selected);
    if (!validation.valid) {
      setFiles([]);
      setLocalError(validation.error || 'Invalid file selection.');
      return;
    }
    setFiles(selected);
    setLocalError(null);
  };

  const openFilePicker = () => {
    if (isWorking) return;
    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilesSelected(Array.from(event.target.files || []));
    event.currentTarget.value = '';
  };

  const handleDragEnter = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isWorking) setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = event;
    if (clientX < rect.left || clientX >= rect.right || clientY < rect.top || clientY >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (isWorking) return;
    handleFilesSelected(Array.from(event.dataTransfer.files || []));
  };

  const runAnalysis = async () => {
    const validation = validateCIMTeaserFiles(files);
    if (!validation.valid) {
      setLocalError(validation.error || 'Invalid file selection.');
      return;
    }

    try {
      setLocalError(null);
      setStep('creating');
      setUploadProgress(0);
      const nextProjectId = await createTeaserProject(files[0].name, files.length);
      setProjectId(nextProjectId);

      setStep('uploading');
      const documentIds: string[] = [];
      for (let index = 0; index < files.length; index += 1) {
        const documentId = await uploadDocument(nextProjectId, files[index]);
        documentIds.push(documentId);
        setUploadProgress(Math.round(((index + 1) / files.length) * 35));
      }

      setStep('queued');
      const started = await startTeaserAnalysis(nextProjectId, documentIds);
      setJobId(started.jobId);
      toast.success('CIM Analysis started');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start CIM Analysis';
      setStep('failed');
      setLocalError(message);
      toast.error(message);
    }
  };

  const isWorking = step !== 'idle' && step !== 'failed' && step !== 'complete';

  return (
    <Card className="overflow-hidden border-border bg-card shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15)]">
      <CardContent className="grid gap-8 p-6 md:grid-cols-[1.05fr_0.95fr] md:p-10">
        <div className="flex flex-col justify-between gap-8">
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-3">
              <Badge className="border-primary/30 bg-primary/10 text-primary hover:bg-primary/10">
                New
              </Badge>
              <span className="text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground">
                Fast first read
              </span>
            </div>

            <div className="space-y-3">
              <h2 className="font-serif text-4xl font-normal tracking-[-0.04em] text-foreground md:text-5xl">
                CIM Analysis
              </h2>
              <p className="max-w-2xl text-base leading-7 text-muted-foreground">
                Upload a CIM or small document bundle and get an AI-powered teaser with the company snapshot,
                key metrics, merits, red flags, missing data, and next diligence questions.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              {['1-5 documents', 'PDF / Word / Excel / Decks', 'PDF + DOCX exports'].map((label) => (
                <div key={label} className="border border-border bg-background/60 p-3">
                  <CheckCircle2 className="mb-2 h-4 w-4 text-primary" />
                  <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="button" onClick={openFilePicker} variant="outline" disabled={isWorking}>
              <UploadCloud className="mr-2 h-4 w-4" />
              Choose files
            </Button>
            <Button type="button" onClick={runAnalysis} disabled={files.length === 0 || isWorking}>
              {isWorking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRight className="mr-2 h-4 w-4" />}
              Generate teaser
            </Button>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              multiple
              accept={ACCEPTED_TYPES}
              onChange={handleInputChange}
              disabled={isWorking}
              aria-label="Select CIM files for quick analysis"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={openFilePicker}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          disabled={isWorking}
          className={cn(
            'group relative min-h-[320px] cursor-pointer border border-border bg-foreground p-6 text-left text-background transition-colors',
            'hover:border-primary/70 disabled:cursor-not-allowed disabled:opacity-80',
            isDragging && 'border-primary bg-primary text-primary-foreground shadow-[0_20px_50px_-12px_hsl(var(--primary)/0.5)]'
          )}
        >
          <div className="absolute right-6 top-6 rounded-full border border-background/20 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-background/70">
            Quick screen
          </div>
          <div className="flex h-full flex-col justify-between">
            <div className="space-y-5">
              <div className="flex h-14 w-14 items-center justify-center border border-background/20 bg-background/10">
                <FileText className="h-7 w-7" />
              </div>
              <div>
                <p className="text-2xl font-medium tracking-[-0.03em]">
                  {isDragging
                    ? 'Release to attach files'
                    : files.length > 0
                      ? `${files.length} file${files.length > 1 ? 's' : ''} selected`
                      : 'Drop in the CIM'}
                </p>
                <p className="mt-2 text-sm leading-6 text-background/70">
                  {files.length > 0
                    ? files.map((file) => file.name).join(', ')
                    : 'Drag files here or click anywhere in this panel to browse.'}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.18em] text-background/65">
                <span>{flowLabel(step)}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-1 bg-background/20" />
              <p className="min-h-[20px] text-xs text-background/60">
                {job?.progress?.message || localError || 'The report will open automatically when analysis is complete.'}
              </p>
            </div>
          </div>
        </button>
      </CardContent>
    </Card>
  );
};
